import { getExplorerBaseUrl } from '@/lib/blockscout';
import { getChainConfig } from '@/chains';

export type ExplorerApiKind = 'blockscout' | 'etherscan';

export interface ExplorerApiConfig {
  kind: ExplorerApiKind;
  /**
   * Blockscout: site origin (e.g. https://eth-sepolia.blockscout.com)
   * Etherscan-compatible: full `/api` endpoint
   *   (e.g. https://api.routescan.io/v2/network/testnet/evm/43113/etherscan/api)
   */
  apiBaseUrl: string;
}

export interface BridgeStatsMeta {
  chainId: number;
  bridgeName: string;
  tokenSymbol: string;
  tokenDecimals: number;
}

const COTI_TESTNET_CHAIN_ID = 7082400;
const COTI_MAINNET_CHAIN_ID = 2632500;
const SEPOLIA_CHAIN_ID = 11155111;
const AVALANCHE_FUJI_CHAIN_ID = 43113;
const ETHEREUM_MAINNET_CHAIN_ID = 1;
const AVALANCHE_C_CHAIN_ID = 43114;

const KNOWN_STATS_CHAIN_IDS = [
  COTI_TESTNET_CHAIN_ID,
  COTI_MAINNET_CHAIN_ID,
  SEPOLIA_CHAIN_ID,
  AVALANCHE_FUJI_CHAIN_ID,
  ETHEREUM_MAINNET_CHAIN_ID,
  AVALANCHE_C_CHAIN_ID,
] as const;

/**
 * Resolves which explorer HTTP API to use for backoffice statistics.
 * Human-facing links still come from getExplorerBaseUrl / chain config —
 * Fuji's SnowScan UI is not a working Blockscout API, so we use Routescan.
 */
export function getExplorerApiConfig(chainId: number): ExplorerApiConfig {
  switch (chainId) {
    case AVALANCHE_FUJI_CHAIN_ID:
      return {
        kind: 'etherscan',
        apiBaseUrl:
          'https://api.routescan.io/v2/network/testnet/evm/43113/etherscan/api',
      };
    case AVALANCHE_C_CHAIN_ID:
      return {
        kind: 'etherscan',
        apiBaseUrl:
          'https://api.routescan.io/v2/network/mainnet/evm/43114/etherscan/api',
      };
    case SEPOLIA_CHAIN_ID:
      return {
        kind: 'blockscout',
        apiBaseUrl: 'https://eth-sepolia.blockscout.com',
      };
    case ETHEREUM_MAINNET_CHAIN_ID:
      return {
        kind: 'etherscan',
        apiBaseUrl: 'https://api.etherscan.io/api',
      };
    case COTI_TESTNET_CHAIN_ID:
      return {
        kind: 'blockscout',
        apiBaseUrl: 'https://testnet.cotiscan.io',
      };
    case COTI_MAINNET_CHAIN_ID:
      return {
        kind: 'blockscout',
        apiBaseUrl: 'https://mainnet.cotiscan.io',
      };
    default:
      return {
        kind: 'blockscout',
        apiBaseUrl: getExplorerBaseUrl(chainId),
      };
  }
}

/** PoD PrivacyPortal + COTI bridge selectors → Blockscout-style method names. */
export const SELECTOR_TO_METHOD: Record<string, string> = {
  // PoD portals
  '0xce88b439': 'deposit',
  '0xee1416ae': 'depositNative',
  '0x2ca5b32c': 'requestWithdrawWithPermit',
  // COTI bridges (for etherscan-style explorers if ever used)
  '0xe2bbb158': 'deposit',
  '0x00aeef8a': 'deposit',
  '0xa41fe49f': 'withdraw',
};

/**
 * Reads the Nth uint256 ABI arg (0-based) after the 4-byte selector.
 * Returns decimal string wei, or null.
 */
export function decodeNthUint256(input: string, index: number): string | null {
  const hex = input.startsWith('0x') || input.startsWith('0X') ? input.slice(2) : input;
  const start = 8 + index * 64;
  const end = start + 64;
  if (hex.length < end || index < 0) return null;
  try {
    return BigInt(`0x${hex.slice(start, end)}`).toString();
  } catch {
    return null;
  }
}

/** @deprecated Prefer decodeNthUint256(input, 1) — kept for call-site clarity. */
export function decodeSecondUint256Amount(input: string): string | null {
  return decodeNthUint256(input, 1);
}

/**
 * Amount arg index by method:
 * - PoD deposit / depositNative / requestWithdrawWithPermit: 2nd arg (after recipient)
 * - COTI withdraw(uint256 amount, ...): 1st arg
 * - COTI deposit(uint256,uint256[,uint256]): timestamps only — no token amount in calldata
 */
export function amountArgIndexForMethod(method: string | null | undefined): number | null {
  if (!method) return null;
  switch (method.toLowerCase()) {
    case 'deposit':
    case 'depositnative':
    case 'requestwithdrawwithpermit':
      return 1;
    case 'withdraw':
      return 0;
    default:
      return null;
  }
}

/**
 * Decode portal/bridge amount from raw calldata using our known layouts.
 * Prefer this over explorer decoded_input — Blockscout often verifies PoD
 * portals against the wrong ABI and mis-labels `amount` as the portal fee.
 */
export function decodeAmountFromCalldata(
  method: string | null | undefined,
  input: string | null | undefined,
): string | null {
  if (!input || !method) return null;
  const hex = input.startsWith('0x') || input.startsWith('0X') ? input.slice(2) : input;
  const normalized = method.toLowerCase();

  if (normalized === 'deposit') {
    // PoD: deposit(address recipient, uint256 amount, uint256, uint256) — 4 words
    if (hex.length >= 8 + 64 * 4) return decodeNthUint256(input, 1);
    // COTI ERC20: deposit(uint256 amount, uint256, uint256) — 3 words
    if (hex.length >= 8 + 64 * 3) return decodeNthUint256(input, 0);
    // COTI native: deposit(uint256, uint256) — timestamps only; use tx.value
    return null;
  }
  if (normalized === 'depositnative' || normalized === 'requestwithdrawwithpermit') {
    return decodeNthUint256(input, 1);
  }
  if (normalized === 'withdraw') {
    return decodeNthUint256(input, 0);
  }
  return null;
}

export function methodFromSelector(methodId: string | undefined | null): string | null {
  if (!methodId) return null;
  const key = methodId.toLowerCase();
  return SELECTOR_TO_METHOD[key] ?? null;
}

function metaForChainAddress(chainId: number, bridgeAddress: string): BridgeStatsMeta | null {
  const cfg = getChainConfig(chainId);
  if (!cfg) return null;
  const target = bridgeAddress.toLowerCase();
  for (const token of cfg.tokens) {
    if (token.isPrivate || !token.bridgeAddressKey) continue;
    const addr = cfg.addresses[token.bridgeAddressKey];
    if (addr?.toLowerCase() === target) {
      return {
        chainId,
        bridgeName: `${token.symbol} Bridge`,
        tokenSymbol: token.symbol,
        tokenDecimals: token.decimals,
      };
    }
  }
  return null;
}

/**
 * Resolve bridge display meta (and owning chain) from the portal/bridge address.
 * Prefers `preferredChainId` when provided, then scans known chains — so a hard
 * refresh still recovers decimals/symbol without location.state.
 */
export function resolveBridgeStatsMeta(
  bridgeAddress: string,
  preferredChainId?: number | null,
): BridgeStatsMeta | null {
  if (!/^0x[0-9a-fA-F]{40}$/.test(bridgeAddress)) return null;
  if (preferredChainId != null) {
    const preferred = metaForChainAddress(preferredChainId, bridgeAddress);
    if (preferred) return preferred;
  }
  for (const chainId of KNOWN_STATS_CHAIN_IDS) {
    if (chainId === preferredChainId) continue;
    const hit = metaForChainAddress(chainId, bridgeAddress);
    if (hit) return hit;
  }
  return null;
}
